import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { SupportAccessReasonDialog } from '@/components/support/SupportAccessReasonDialog';
import { toast } from '@/hooks/use-toast';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { format } from 'date-fns';

interface BugReport {
  id: string;
  created_at: string;
  updated_at: string;
  summary: string;
  trying_to_do: string;
  what_happened: string;
  frequency: string;
  severity: string;
  status: string;
  environment: string;
  shop_name: string;
  user_email: string;
  route_path: string;
  browser_info: string;
  context_json: any;
  plan_tier: string;
  internal_notes: string;
  has_attachments: boolean;
  tenant_id: string;
}

export default function BugReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { membership } = useAuth();
  const { canAccessSupportMode, startSupportSession } = useSupportMode();
  const [report, setReport] = useState<BugReport | null>(null);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isStartingSupport, setIsStartingSupport] = useState(false);
  const [status, setStatus] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);

  // Check if user is manager
  useEffect(() => {
    if (membership && membership.role !== 'MANAGER') {
      navigate('/');
    }
  }, [membership, navigate]);

  // Fetch bug report
  useEffect(() => {
    const fetchReport = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        // R125-M-2: explicit column list — detail view does need most
        // fields (summary/severity/shop_name/user_email/trying_to_do/
        // what_happened/context_json etc.) but select('*') would auto-
        // expose any future internal columns (e.g. admin_notes_private,
        // triage_user_id, etc.) without a review.
        const { data, error } = await supabase
          .from('bug_reports')
          .select('id, created_at, updated_at, summary, shop_name, user_email, severity, status, environment, plan_tier, tenant_id, trying_to_do, what_happened, frequency, route_path, browser_info, context_json, internal_notes, has_attachments')
          .eq('id', id)
          .single();

        if (error) throw error;
        
        setReport(data);
        setStatus(data.status);
        setInternalNotes(data.internal_notes || '');

        // P6-B2 — bug_attachments bucket flip from public to private
        // (migration 20260517120000_p6b2_bug_attachments_private.sql).
        //
        // Pre-B2 this block called `getPublicUrl` per file and pushed the
        // resulting public URLs into `attachments`. The `bug_attachments`
        // bucket is now private; the new SELECT policy gates access to
        // the bug-report owner OR a manager of the bug's shop, mirroring
        // the bug_reports table's SELECT semantics. Public URLs no longer
        // resolve, so we batch-resolve signed URLs via `createSignedUrls`
        // (canonical pattern from `useDviMedia.ts:47-49`, mirrored by
        // P6-B1's CertificationUploader / AssetPhotosTab fixes).
        //
        // The 3600-second expiry matches the canonical batch-signed-URL
        // TTL across the codebase. The existing `useEffect` `[id]` deps
        // already prevent per-render re-fetches, so this block runs once
        // per bug-report navigation — not once per render.
        if (data.has_attachments) {
          // CORE-BK responsibility 4. Attachments are no longer signed from the
          // browser: releasing a private support artefact is a disclosure, and
          // the client is not a place where that can be recorded before it
          // happens. The server authorises, records, then signs.
          const { data: signedResult, error: signErr } =
            await supabase.functions.invoke('get-support-attachment-urls', {
              body: { bug_report_id: id },
            });
          if (signErr) {
            console.warn('[BugReportDetail] attachment release failed:', signErr);
          } else if (Array.isArray(signedResult?.urls)) {
            setAttachments(signedResult.urls as string[]);
          }
        }
      } catch (error) {
        console.error('Error fetching bug report:', error);
        toast({
          title: 'Error',
          description: 'Failed to load bug report',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handleSave = async () => {
    if (!report) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('bug_reports')
        .update({
          status,
          internal_notes: internalNotes,
        })
        .eq('id', report.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Bug report updated',
      });
    } catch (error) {
      console.error('Error updating bug report:', error);
      toast({
        title: 'Error',
        description: 'Failed to update bug report',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // BJ-N5/BJ-N6: the operator states a disclosure-safe reason before the shop
  // opens. The dialog is offered a SAFE GENERIC default — never the report
  // summary, internal notes or the customer's description, which are
  // customer-authored and may contain personal data. `note` keeps the specific
  // investigative context, privately.
  const handleOpenInSupportMode = () => {
    if (!report || !canAccessSupportMode) return;
    setReasonDialogOpen(true);
  };

  const handleConfirmSupportAccess = async (disclosureReason: string) => {
    if (!report || !canAccessSupportMode) return;

    setIsStartingSupport(true);
    try {
      // Parse context to determine best landing page
      const context = report.context_json || {};
      const boardDate = context.boardDate;
      const currentPath = window.location.pathname;

      await startSupportSession({
        targetShopId: report.tenant_id,
        disclosureReason,
        mode: 'SHOP',
        triggeredFromBugId: report.id,
        note: `Investigating: ${report.summary}`,
        returnPath: currentPath, // Save current path to return to
      });

      // Navigate to relevant page based on context
      if (boardDate) {
        navigate(`/shop-floor?date=${boardDate}`);
      } else if (context.ticketId) {
        navigate(`/shop-floor`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error starting support mode:', error);
    } finally {
      setIsStartingSupport(false);
    }
  };

  if (membership?.role !== 'MANAGER' || isLoading || !report) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="container mx-auto px-4 py-8">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/bug-reports')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Reports
          </Button>
          
          {canAccessSupportMode && (
            <Button
              onClick={handleOpenInSupportMode}
              disabled={isStartingSupport}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              {isStartingSupport ? 'Starting...' : 'Open in Support Mode'}
            </Button>
          )}
        </div>

        <div className="bg-card rounded-lg border p-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold mb-2">{report.summary}</h1>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">
                {format(new Date(report.created_at), 'MMM d, yyyy h:mm a')}
              </Badge>
              <Badge variant="outline">{report.environment}</Badge>
              <Badge>{report.severity}</Badge>
            </div>
          </div>

          {/* Shop & User Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Shop</p>
              <p className="font-medium">{report.shop_name}</p>
              <p className="text-xs text-muted-foreground">{report.plan_tier}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">User</p>
              <p className="font-medium">{report.user_email}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold">What they were trying to do:</Label>
              <p className="mt-2 p-3 bg-muted rounded">{report.trying_to_do}</p>
            </div>

            <div>
              <Label className="text-base font-semibold">What happened instead:</Label>
              <p className="mt-2 p-3 bg-muted rounded">{report.what_happened}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency</Label>
                <p className="mt-1 text-sm">{report.frequency}</p>
              </div>
              <div>
                <Label>Severity</Label>
                <p className="mt-1 text-sm">{report.severity}</p>
              </div>
            </div>
          </div>

          {/* Technical Context */}
          <div>
            <Label className="text-base font-semibold">Technical Context</Label>
            <pre className="mt-2 p-4 bg-muted rounded text-xs overflow-x-auto">
{`Route: ${report.route_path || 'N/A'}
Browser: ${report.browser_info || 'N/A'}
Ticket ID: ${report.context_json?.ticketId || 'N/A'}
Board Date: ${report.context_json?.boardDate || 'N/A'}
Viewport: ${report.context_json?.viewport?.width || 'N/A'}x${report.context_json?.viewport?.height || 'N/A'}
User Agent: ${report.context_json?.userAgent || 'N/A'}`}
            </pre>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <Label className="text-base font-semibold">Screenshots</Label>
              <div className="mt-2 grid grid-cols-2 gap-4">
                {attachments.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src={url}
                      alt={`Screenshot ${index + 1}`}
                      className="rounded border hover:opacity-80 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Status & Notes */}
          <div className="space-y-4 pt-6 border-t">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={4}
                placeholder="Add notes visible only to managers..."
              />
            </div>

            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </main>

      <SupportAccessReasonDialog
        open={reasonDialogOpen}
        onOpenChange={setReasonDialogOpen}
        targetLabel={report?.shop_name ?? 'this shop'}
        defaultReason="Investigating a reported product issue"
        onConfirm={handleConfirmSupportAccess}
      />
    </div>
  );
}
